import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { requestHandler } from '../utils/requestHandler';
import { CONTENT_ENDPOINT } from '../utils/constants/controlCenterConstants';
import { isMock, setErrorInAppState } from '../utils/helpers';
import { contentResponse } from '../mocks/mockContentResponse';
import { AppState, ContentSlice } from '../interfaces/store.interface';

const defaultContent = {
  header: {},
  footer: {},
  accounts: {},
  errors: {
    genericErrors: {
      linkNotValid: {},
      notAuthenticated: {},
      stopByPage: {},
      systemDown: {},
      accountNotFound: {},
      sessionTimeoutError: {},
    },
    clientSpecificErrors: {
      linkNotValid: {},
      notAuthenticated: {},
      stopByPage: {},
      systemDown: {},
      accountNotFound: {},
      sessionTimeoutError: {},
    },
  },
  singleUseCard: {},
  shoppingPass: {},
  profileMenu: {},
  sessionTimeOut: {},
  thankYou: {},
  salutation: {},
  customerVerification: {},
  cancelModal: {},
  creditLimitIncrease: {
    cliResults: {},
  },
};
/**
 * Redux "content" state representation for  will look like content:{copyContent:{},data:{},lang:'EN' ...}
 * copyContent is for all language content and it is api response from control center
 * data in redux is content for specific language getting set on toggle language coming from
 **/
export const initialState: ContentSlice = {
  copyContent: {},
  data: defaultContent,
  lang: 'EN',
  loadApp: false,
  errorLoadingContent: false,
};

export const content = 'content';

export const fetchStaticContent = createAsyncThunk(
  `${content}/fetchStaticContent`,
  async () => {
    try {
      if (isMock()) {
        return contentResponse.data;
      } else {
        const { data }: any = await requestHandler({
          customHeaders: {
            'Content-Type': 'application/json',
          },
          skipAuthToken: true,
        }).get(CONTENT_ENDPOINT);
        return data;
      }
    } catch (error: any) {
      const errorObject = error?.response || error;
      setErrorInAppState(errorObject, 'content');
      return error;
    }
  },
);

export const contentSlice = createSlice({
  name: content,
  initialState,
  reducers: {
    setLanguageContent: (state: any, action) => {
      state.lang = action.payload;
      state.data = state.copyContent[state.lang];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchStaticContent.fulfilled,
      (state: any, { payload }: any) => {
        state.loadApp = true;
        state.copyContent = payload;
        state.data = payload[state.lang];
      },
    );
    builder.addCase(fetchStaticContent.rejected, (state: any) => {
      state.loadApp = true; // this can be flipped to false to show error page
      state.errorLoadingContent = true;
      state.copyContent = initialState.data; // later on the error can be posted in this case
    });
  },
});
// state actions can be exported here
export const { setLanguageContent } = contentSlice.actions;
export default contentSlice.reducer;

// state getters can be written & exported here
export const isAppReadyWithStaticContent = (state: AppState) =>
  state.content.loadApp;
export const isErrorLoadingContent = (state: AppState) =>
  state.content.errorLoadingContent;
export const getHeaderContent = (state: AppState) => state.content.data?.header;
export const getFooterContent = (state: AppState) => state.content.data?.footer;
export const getAccountDetailsContent = (state: AppState) =>
  state.content.data?.accounts;

// error messages getters
const getclientSpecificErrors = (state: any, key: string, clientId: string) => {
  const { clientSpecificErrors } = state.content.data.errors;
  return clientSpecificErrors[clientId]?.[key];
};

const getGenericErrors = (state: any, key: any) => {
  const { genericErrors } = state.content.data.errors;
  return genericErrors?.[key];
};
export const getErrorMessages = createSelector(
  [getclientSpecificErrors, getGenericErrors],
  (clientSpecificErrors, genericErrors) => {
    let heading = '';
    let message = '';
    let subHeading = '';
    let contentTitle = '';
    let contentLine1 = '';
    let contentLine2 = '';
    let contentLine3 = '';
    let contentLine4 = '';
    let contentLine5 = '';

    if (clientSpecificErrors) {
      heading = clientSpecificErrors['heading'];
      message = clientSpecificErrors['message'];
      subHeading = clientSpecificErrors['subHeading'];
      contentTitle = clientSpecificErrors['contentTitle'];
      contentLine1 = clientSpecificErrors['contentLine1'];
      contentLine2 = clientSpecificErrors['contentLine2'];
      contentLine3 = clientSpecificErrors['contentLine3'];
      contentLine4 = clientSpecificErrors['contentLine4'];
      contentLine5 = clientSpecificErrors['contentLine5'];
    } else if (genericErrors) {
      heading = genericErrors['heading'];
      message = genericErrors['message'];
    }
    return {
      heading,
      message,
      subHeading,
      contentTitle,
      contentLine1,
      contentLine2,
      contentLine3,
      contentLine4,
      contentLine5,
    };
  },
);

export const getAccountNotFoundErrorContent = (
  state: AppState,
  clientId: string,
) => getErrorMessages(state, 'accountNotFound', clientId);

export const getLinkNotValidErrorContent = (state: AppState) =>
  state.content.data?.errors.genericErrors.linkNotValid;

export const getNotAuthenticatedErrorContent = (
  state: AppState,
  clientId: string,
) => getErrorMessages(state, 'notAuthenticated', clientId);

export const getStopByPageErrorContent = (state: AppState, clientId: string) =>
  getErrorMessages(state, 'stopByPage', clientId);

export const getSystemDownErrorContent = (state: AppState, clientId: string) =>
  getErrorMessages(state, 'systemDown', clientId);

export const getSessionTimeoutErrorContent = (state: AppState) =>
  state.content.data?.errors.genericErrors.sessionTimeoutError;
//error messages getter ends

export const getProfileMenuContent = (state: AppState) =>
  state.content.data?.profileMenu;
export const getSessionTimeoutContent = (state: AppState) =>
  state.content.data?.sessionTimeOut;
export const getThankYouContent = (state: AppState) =>
  state.content.data?.thankYou;
export const getCustomerVerificationContent = (state: AppState) =>
  state.content.data?.customerVerification;
export const getSalutationContent = (state: AppState) =>
  state.content.data?.salutation;
export const getSectionTitle = (state: AppState) =>
  state.content.data?.accounts;
export const getCancelModalContent = (state: AppState) =>
  state.content.data?.cancelModal;
export const getSingleUseCardContent = (state: AppState) =>
  state.content.data?.singleUseCard;
export const getShoppingPassContent = (state: AppState) =>
  state.content.data?.shoppingPass;
export const getLanguage = (state: AppState) => state.content.lang;
export const getCreditLimitIncreaseResultContent = (state: AppState) =>
  state.content.data?.creditLimitIncrease?.cliResults;
export const getCreditLimitIncreaseContent = (state: AppState) =>
  state.content.data?.creditLimitIncrease;
export const getRetrieveAccountErrorContent = (
  state: AppState,
  clientId: string,
) => getErrorMessages(state, 'lowesAccountError', clientId);
